import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Space,
  Input,
  Row,
  Col,
  Modal,
  Form,
  message,
  Upload,
  Select,
  DatePicker,
} from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  DownloadOutlined,
  UploadOutlined,
  PrinterOutlined,
} from '@ant-design/icons';
import api, { ASSETS_BASE_URL } from '../api';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';

const imagePath = `${ASSETS_BASE_URL}/`;

const Member = () => {
  const [searchText, setSearchText] = useState('');
  const [searchAccno, setSearchAccno] = useState('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentRecord, setCurrentRecord] = useState(null);
  const [form] = Form.useForm();
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    setFilteredData(data.filter(item => item.isactive !== 'RQ'));
  }, [data]);


  const fetchData = async () => {
    try {
      const response = await api.get('/member/get-all-members');
      if (response.data && Array.isArray(response.data.data)) {
        const sortedData = response.data.data
          .map(item => ({
            ...item,
            isactive: item.isactive || 'Active', // Use string from DB
            joiningdate: item.joiningdate ? dayjs(item.joiningdate).format('YYYY-MM-DD') : null,
            Dob: item.Dob ? dayjs(item.Dob).format('YYYY-MM-DD') : null,
            WeddingAnnversery: item.WeddingAnnversery ? dayjs(item.WeddingAnnversery).format('YYYY-MM-DD') : null,
          }))
          .sort((a, b) => (a.SerialNo || 0) - (b.SerialNo || 0));

        const storedData = JSON.parse(localStorage.getItem('membersData')) || [];
        const mergedData = sortedData.map(apiItem => {
          const localItem = storedData.find(local => local._id === apiItem._id);
          return {
            ...apiItem,
            isactive: localItem?.isactive || apiItem.isactive || 'Active',
          };
        });

        setData(mergedData);
        setFilteredData(mergedData);
        localStorage.setItem('membersData', JSON.stringify(mergedData));
      } else {
        throw new Error('Unexpected response structure');
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      message.error('Failed to fetch data. Using local storage data if available.');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === 'N/A') return 'N/A';
    const date = dayjs(dateString, ['YYYY-MM-DD'], true);
    if (!date.isValid()) {
      console.warn(`Invalid date format: ${dateString}`);
      return 'N/A';
    }
    return date.format('YYYY-MM-DD');
  };

  const cleanText = (text) => {
    return text ? text.replace(/\s+/g, ' ').trim() : 'N/A';
  };

  // NEW: Update isactive string via updateMember API
  const handleStatusChange = async (id, newStatus) => {
    if (!['Active', 'Inactive', 'Deactive', 'Nomore', 'RQ'].includes(newStatus)) return;

    const oldRecord = data.find(m => m._id === id);
    if (!oldRecord || oldRecord.isactive === newStatus) return;

    const oldStatus = oldRecord.isactive;

    // Optimistic UI update
    const optimisticData = data.map(item =>
      item._id === id ? { ...item, isactive: newStatus } : item
    );
    setData(optimisticData);
    localStorage.setItem('membersData', JSON.stringify(optimisticData));

    try {
      const response = await api.put(
        `/member/updateMember/${id}`,
        { isactive: newStatus },
        { headers: { 'Content-Type': 'application/json' } }
      );

      console.log('Status update response:', response.data);

      if (response.status >= 200 && response.status < 300) {
        message.success(`Status changed to ${newStatus}`);
      } else {
        throw new Error(`Server error: ${response.status}`);
      }
    } catch (error) {
      console.error('Failed to update status:', error);

      // Rollback
      setData(prev => prev.map(item =>
        item._id === id ? { ...item, isactive: oldStatus } : item
      ));
      localStorage.setItem('membersData', JSON.stringify(data));

      message.error(
        error.response?.data?.message ||
        'Failed to update status. Please try again.'
      );
    }
  };

  const handleExportExcel = () => {
    const sortedData = [...data].sort((a, b) => a.Accno.localeCompare(b.Accno));
    const exportData = sortedData.map((member, index) => ({
      'S No': index + 1,
      'ACCI Code': member.Accno || 'N/A',
      'Member Name': member.Name || 'N/A',
      'Mobile': member.Mobileno || 'N/A',
      'Alternative Mobile': member.MobileAlternativeno || 'N/A',
      'Company Name': member.CompanyName || 'N/A',
      'Business Category': member.BusinessCategory || 'N/A',
      'Group Incharge': member.groupincharge || 'N/A',
      'Email': member.Email || 'N/A',
      'Address': member.Address || 'N/A',
      'Profile': member.profile || 'N/A',
      'Joining Date': member.joiningdate || 'N/A',
      'Date of Birth': member.Dob || 'N/A',
      'Wedding Anniversary': member.WeddingAnnversery || 'N/A',
      'Status': member.isactive || 'Active', // Real string
    }));
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Members Data');
    XLSX.writeFile(workbook, 'members_data.xlsx');
  };

  const handleExportPDF = async () => {
    const doc = new jsPDF();
    const cardWidth = 90;
    const cardHeight = 80;
    const startX = 10;
    const startY = 10;
    const spacing = 10;
    let x = startX;
    let y = startY;
    const corsProxy = `${ASSETS_BASE_URL}/`;

    const getBase64Image = async (url) => {
      try {
        const response = await fetch(url, { mode: 'cors' });
        if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
        const blob = await response.blob();
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = () => reject(new Error('Failed to convert image to Base64'));
          reader.readAsDataURL(blob);
        });
      } catch (error) {
        console.error(`Failed to fetch image from ${url}:`, error);
        return null;
      }
    };

    const acciLogoUrl = `${ASSETS_BASE_URL}/proxy-image?url=public/logo.png`;
    let acciLogo = await getBase64Image(acciLogoUrl);
    if (acciLogo) {
      doc.addImage(acciLogo, "PNG", 60, 30, 80, 40);
    }
    doc.setFontSize(24);
    doc.setFont("helvetica", "bold");
    doc.text("Member Bio Data", 105, 80, { align: "center" });
    doc.setDrawColor(0);
    doc.setLineWidth(1);
    doc.line(50, 85, 160, 85);
    doc.addPage();

    const getImageFormat = (base64String) => {
      const match = base64String.match(/^data:image\/(\w+);base64,/);
      return match ? match[1].toUpperCase() : 'JPEG';
    };

    const sortedData = [...data].sort((a, b) => a.Accno.localeCompare(b.Accno));
    for (const member of sortedData) {
      if (x + cardWidth > doc.internal.pageSize.width - 10) {
        x = startX;
        y += cardHeight + spacing;
      }
      if (y + cardHeight > doc.internal.pageSize.height - 10) {
        doc.addPage();
        x = startX;
        y = startY;
      }
      doc.setFillColor(240, 248, 255);
      doc.rect(x, y, cardWidth, cardHeight, 'F');
      doc.setDrawColor(0);
      doc.rect(x, y, cardWidth, cardHeight);

      let base64Image = null;
      if (member.profile && member.profile !== 'N/A') {
        const profilePath = member.profile.replace(/\\/g, '/');
        // Use proxy with relative path to avoid CORS errors in PDF generation
        const imagePath = `${ASSETS_BASE_URL}/proxy-image?url=${encodeURIComponent(profilePath)}`;
        base64Image = await getBase64Image(imagePath);
      }

      if (base64Image) {
        const format = getImageFormat(base64Image);
        doc.addImage(base64Image, format, x + 5, y + 5, 20, 20);
      } else {
        doc.setFontSize(8);
        doc.text('Image Unavailable', x + 10, y + 15);
      }

      const textStartX = x + 30;
      let textStartY = y + 7;
      doc.setFontSize(6);
      doc.setTextColor(0, 0, 0);

      const accNo = cleanText(member.Accno);
      const name = cleanText(member.Name);
      const email = cleanText(member.Email);
      const mobile = cleanText(member.Mobileno);
      const company = cleanText(member.CompanyName);
      const designation = cleanText(member.Designation || 'N/A');
      const address = cleanText(member.Address);
      const joiningDate = member.joiningdate || 'N/A';
      const dob = member.Dob || 'N/A';
      const weddingAnniversary = member.WeddingAnnversery || 'N/A';
      const status = member.isactive || 'Active';

      const wrappedAccno = doc.splitTextToSize(`ACCI NO: ${accNo}`, cardWidth - 35);
      doc.text(wrappedAccno, textStartX, textStartY);
      textStartY += wrappedAccno.length * 5;

      const wrappedName = doc.splitTextToSize(`Name: ${name}`, cardWidth - 35);
      doc.text(wrappedName, textStartX, textStartY);
      textStartY += wrappedName.length * 5;

      const wrappedDesignation = doc.splitTextToSize(`Designation: ${designation}`, cardWidth - 35);
      doc.text(wrappedDesignation, textStartX, textStartY);
      textStartY += wrappedDesignation.length * 5;

      doc.text(`Mobile: ${mobile}`, textStartX, textStartY);
      textStartY += 7;

      const wrappedEmail = doc.splitTextToSize(`Email: ${email}`, cardWidth - 35);
      doc.text(wrappedEmail, textStartX, textStartY);
      textStartY += wrappedEmail.length * 5;

      const wrappedAddress = doc.splitTextToSize(`Address: ${address}`, cardWidth - 35);
      doc.text(wrappedAddress, textStartX, textStartY);
      textStartY += wrappedAddress.length * 5;

      const wrappedCompany = doc.splitTextToSize(`Company: ${company}`, cardWidth - 35);
      doc.text(wrappedCompany, textStartX, textStartY);
      textStartY += wrappedCompany.length * 5;

      doc.text(`Joining Date: ${joiningDate}`, textStartX, textStartY);
      textStartY += 7;

      doc.text(`Date of Birth: ${dob}`, textStartX, textStartY);
      textStartY += 7;

      doc.text(`Wedding Anniversary: ${weddingAnniversary}`, textStartX, textStartY);
      textStartY += 7;

      doc.text(`Status: ${status}`, textStartX, textStartY);

      x += cardWidth + spacing;
    }

    const pdfBlob = doc.output('blob');
    const pdfURL = URL.createObjectURL(pdfBlob);
    window.open(pdfURL, '_blank');
  };

  const handlePrintSingleMemberPDF = async (member) => {
    const doc = new jsPDF();
    const cardWidth = 180;
    const cardHeight = 140;
    const startX = 15;
    const startY = 15;
    const corsProxy = `${ASSETS_BASE_URL}/`;

    const getBase64Image = async (url) => {
      try {
        const response = await fetch(url, { mode: 'cors' });
        if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
        const blob = await response.blob();
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = () => reject(new Error('Failed to convert image to Base64'));
          reader.readAsDataURL(blob);
        });
      } catch (error) {
        console.error(`Failed to fetch image from ${url}:`, error);
        return null;
      }
    };

    const getImageFormat = (base64String) => {
      const match = base64String.match(/^data:image\/(\w+);base64,/);
      return match ? match[1].toUpperCase() : 'JPEG';
    };

    const acciLogoUrl = `${ASSETS_BASE_URL}/proxy-image?url=public/logo.png`;
    let acciLogo = await getBase64Image(acciLogoUrl);
    if (acciLogo) {
      doc.addImage(acciLogo, "PNG", 65, 10, 80, 40);
    }

    doc.setFontSize(20);
    doc.setFont("helvetica", "bold");
    doc.text("Member Bio Data", 105, 60, { align: "center" });
    doc.setDrawColor(0);
    doc.setLineWidth(1);
    doc.line(50, 65, 160, 65);

    doc.setFillColor(240, 248, 255);
    doc.rect(startX, startY + 60, cardWidth, cardHeight, 'F');
    doc.setDrawColor(0);
    doc.rect(startX, startY + 60, cardWidth, cardHeight);

    let base64Image = null;
    if (member.profile && member.profile !== 'N/A') {
      const profilePath = member.profile.replace(/\\/g, '/');
      // Use proxy with relative path to avoid CORS errors in PDF generation
      const imagePath = `${ASSETS_BASE_URL}/proxy-image?url=${encodeURIComponent(profilePath)}`;
      base64Image = await getBase64Image(imagePath);
    }

    if (base64Image) {
      const format = getImageFormat(base64Image);
      doc.addImage(base64Image, format, startX + 5, startY + 65, 30, 30);
    } else {
      doc.setFontSize(10);
      doc.text('Image Unavailable', startX + 10, startY + 80);
    }

    const textStartX = startX + 40;
    let textStartY = startY + 65;
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);

    const accNo = cleanText(member.Accno);
    const name = cleanText(member.Name);
    const email = cleanText(member.Email);
    const mobile = cleanText(member.Mobileno);
    const alternativeMobile = cleanText(member.MobileAlternativeno);
    const company = cleanText(member.CompanyName);
    const businessCategory = cleanText(member.BusinessCategory);
    const address = cleanText(member.Address);
    const groupIncharge = cleanText(member.groupincharge);
    const city1 = cleanText(member.City1);
    const city2 = cleanText(member.City2);
    const village1 = cleanText(member.Village1);
    const village2 = cleanText(member.Village2);
    const joiningDate = member.joiningdate || 'N/A';
    const dob = member.Dob || 'N/A';
    const weddingAnniversary = member.WeddingAnnversery || 'N/A';
    const status = member.isactive || 'Active';

    const wrappedAccno = doc.splitTextToSize(`ACCI NO: ${accNo}`, cardWidth - 45);
    doc.text(wrappedAccno, textStartX, textStartY);
    textStartY += wrappedAccno.length * 6;

    const wrappedName = doc.splitTextToSize(`Name: ${name}`, cardWidth - 45);
    doc.text(wrappedName, textStartX, textStartY);
    textStartY += wrappedName.length * 6;

    doc.text(`Mobile: ${mobile}`, textStartX, textStartY);
    textStartY += 8;

    doc.text(`Alternative Mobile: ${alternativeMobile}`, textStartX, textStartY);
    textStartY += 8;

    const wrappedCompany = doc.splitTextToSize(`Company: ${company}`, cardWidth - 45);
    doc.text(wrappedCompany, textStartX, textStartY);
    textStartY += wrappedCompany.length * 6;

    const wrappedBusinessCategory = doc.splitTextToSize(`Business Category: ${businessCategory}`, cardWidth - 45);
    doc.text(wrappedBusinessCategory, textStartX, textStartY);
    textStartY += wrappedBusinessCategory.length * 6;

    const wrappedEmail = doc.splitTextToSize(`Email: ${email}`, cardWidth - 45);
    doc.text(wrappedEmail, textStartX, textStartY);
    textStartY += wrappedEmail.length * 6;

    const wrappedAddress = doc.splitTextToSize(`Address: ${address}`, cardWidth - 45);
    doc.text(wrappedAddress, textStartX, textStartY);
    textStartY += wrappedAddress.length * 6;

    doc.text(`Group Incharge: ${groupIncharge}`, textStartX, textStartY);
    textStartY += 8;

    doc.text(`City 1: ${city1}`, textStartX, textStartY);
    textStartY += 8;

    doc.text(`City 2: ${city2}`, textStartX, textStartY);
    textStartY += 8;

    doc.text(`Village 1: ${village1}`, textStartX, textStartY);
    textStartY += 8;

    doc.text(`Village 2: ${village2}`, textStartX, textStartY);
    textStartY += 8;

    doc.text(`Joining Date: ${joiningDate}`, textStartX, textStartY);
    textStartY += 8;

    doc.text(`Date of Birth: ${dob}`, textStartX, textStartY);
    textStartY += 8;

    doc.text(`Wedding Anniversary: ${weddingAnniversary}`, textStartX, textStartY);
    textStartY += 8;

    doc.text(`Status: ${status}`, textStartX, textStartY);

    const pdfBlob = doc.output('blob');
    const pdfURL = URL.createObjectURL(pdfBlob);
    window.open(pdfURL, '_blank');
  };

  const columns = [
    {
      title: 'S No',
      key: 'SerialNo',
      render: (text, record, index) => {
        const page = pagination.current || 1;
        const pageSize = pagination.pageSize || 10;
        return (page - 1) * pageSize + index + 1;
      },
    },
    {
      title: 'Photo',
      dataIndex: 'profile',
      key: 'profile',
      render: (profile) => (
        <img
          src={profile && profile !== 'N/A' ? `${imagePath}${profile}` : 'https://via.placeholder.com/50'}
          alt="Profile"
          style={{ width: 50, height: 50, borderRadius: '50%' }}
        />
      ),
    },
    {
      title: 'ACCI Code',
      dataIndex: 'Accno',
      key: 'Accno',
      sorter: (a, b) => a.Accno.localeCompare(b.Accno),
      defaultSortOrder: 'ascend',
    },
    {
      title: 'Member Name',
      dataIndex: 'Name',
      key: 'Name',
    },
    {
      title: 'Company Name',
      dataIndex: 'CompanyName',
      key: 'CompanyName',
    },
    {
      title: 'Email',
      dataIndex: 'Email',
      key: 'Email',
      render: (email) => email || 'N/A',
    },
    {
      title: 'Status',
      key: 'isactive',
      render: (_, record) => (
        <Select
          value={record.isactive || 'Active'}
          style={{ width: 120 }}
          onChange={(val) => handleStatusChange(record._id, val)}
        >
          <Select.Option value="Active">Active</Select.Option>
          <Select.Option value="Inactive">Inactive</Select.Option>
          {/* <Select.Option value="Deactive">Deactive</Select.Option>
          <Select.Option value="Nomore">Nomore</Select.Option> */}
          <Select.Option value="RQ">Archive</Select.Option>
        </Select>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="middle">
          <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          <Button icon={<DeleteOutlined />} onClick={() => handleDelete(record._id)} />
          <Button
            icon={<PrinterOutlined />}
            onClick={() => handlePrintSingleMemberPDF(record)}
            title="Print PDF"
          />
        </Space>
      ),
    },
  ];

  const handleAdd = async (values) => {
    try {
      const formData = new FormData();
      Object.keys(values).forEach((key) => {
        let value = values[key];
        if (['joiningdate', 'Dob', 'WeddingAnnversery'].includes(key)) {
          value = value ? dayjs(value).format('YYYY-MM-DD') : undefined;
        } else if (typeof value === 'string') {
          value = cleanText(value);
        }

        if (key === 'profile' || key === 'registerForm' || key === 'certificate') {
          if (value && value[0]?.originFileObj) {
            formData.append(key, value[0].originFileObj);
          }
        } else {
          if (value !== undefined && value !== null && value !== '') {
            formData.append(key, value);
          }
        }
      });

      const response = await api.post(
        '/member/addMember',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      if (response.data && response.data.data) {
        const newMember = { ...response.data.data, isactive: response.data.data.isactive || 'Active' };
        setData(prev => [...prev, newMember]);
      }
      message.success('Member added successfully');
      fetchData();
    } catch (error) {
      console.error('Failed to add member:', error);
      message.error('Failed to add member. Please try again later.');
    }
  };

  const handleEdit = (record) => {
    form.setFieldsValue({
      ...record,
      joiningdate: record.joiningdate && record.joiningdate !== 'N/A' ? dayjs(record.joiningdate, 'YYYY-MM-DD') : null,
      Dob: record.Dob && record.Dob !== 'N/A' ? dayjs(record.Dob, 'YYYY-MM-DD') : null,
      WeddingAnnversery: record.WeddingAnnversery && record.WeddingAnnversery !== 'N/A' ? dayjs(record.WeddingAnnversery, 'YYYY-MM-DD') : null,
      profile: record.profile && record.profile !== 'N/A'
        ? [{ uid: '-1', name: record.profile.split('/').pop(), status: 'done', url: `${imagePath}${record.profile}` }]
        : [],
      registerForm: record.registerForm && record.registerForm !== 'N/A'
        ? [{ uid: '-1', name: record.registerForm.split('/').pop(), status: 'done', url: `${imagePath}${record.registerForm}` }]
        : [],
      certificate: record.certificate && record.certificate !== 'N/A'
        ? [{ uid: '-1', name: record.certificate.split('/').pop(), status: 'done', url: `${imagePath}${record.certificate}` }]
        : [],
    });
    setEditMode(true);
    setCurrentRecord(record);
    setIsModalVisible(true);
  };

  const handleUpdate = async (_id, values) => {
    try {
      const formData = new FormData();
      Object.entries(values).forEach(([key, value]) => {
        if (['joiningdate', 'Dob', 'WeddingAnnversery'].includes(key)) {
          value = value ? dayjs(value).format('YYYY-MM-DD') : undefined;
        } else if (typeof value === 'string') {
          value = cleanText(value);
        }
        if (['profile', 'registerForm', 'certificate'].includes(key)) {
          const file = Array.isArray(value) && value.length > 0 && value[0]?.originFileObj ? value[0].originFileObj : null;
          if (file instanceof File) formData.append(key, file);
        } else {
          if (value !== undefined && value !== null && value !== '') {
            formData.append(key, value);
          }
        }
      });

      const response = await api.put(
        `/member/updateMember/${encodeURIComponent(_id)}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      if (response.status !== 200) throw new Error(`Status: ${response.status}`);
      await fetchData();
      message.success('Member updated successfully');
      return response.data;
    } catch (error) {
      console.error('Failed to update member:', error);
      message.error(
        error.response?.data?.message ||
        'Failed to update member. Please try again later.'
      );
      throw error;
    }
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      if (editMode) {
        await handleUpdate(currentRecord._id, values);
      } else {
        await handleAdd(values);
      }
      setIsModalVisible(false);
      form.resetFields();
    } catch (error) {
      message.error('Please correct the errors in the form before proceeding.');
    }
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: 'Are you sure you want to delete this record?',
      onOk: async () => {
        try {
          await api.delete(`/member/deleteMember/${id}`);
          setData(data.filter(item => item._id !== id));
          message.success('Member deleted successfully');
        } catch (error) {
          console.error('Failed to delete member:', error);
          message.error('Failed to delete member. Please try again later.');
        }
      },
    });
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
  };

  const handleSearch = () => {
    const searchLower = searchText.toLowerCase();
    const searchNormalized = searchText.replace(/\s+/g, '').toLowerCase();

    const filtered = data
      .filter(item => item.isactive !== 'RQ')
      .filter(item => {
        const nameMatch = (item.Name || '').toLowerCase().includes(searchLower);
        const accnoMatch = (item.Accno || '').replace(/\s+/g, '').toLowerCase().includes(searchNormalized);
        return nameMatch || accnoMatch;
      });
    setFilteredData(filtered);
  };

  return (
    <div>
      <h2 style={{ color: 'teal', marginBottom: '20px' }}>Member List</h2>
      <Row gutter={16} align="middle">
        <Col span={10}>
          <Input.Search
            placeholder="Search by name or ACCI Code (e.g. ACCI0001)"
            onSearch={handleSearch}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            enterButton
          />
        </Col>
        <Col span={7} style={{ textAlign: 'center' }}>
          <Space>
            <Button type="primary" icon={<DownloadOutlined />} onClick={handleExportPDF}>
              Export PDF
            </Button>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleExportExcel}
              style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
            >
              Excel
            </Button>
          </Space>
        </Col>
        <Col span={7} style={{ textAlign: 'right' }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            style={{ backgroundColor: 'teal' }}
            onClick={() => {
              setEditMode(false);
              setCurrentRecord(null);
              form.resetFields();
              setIsModalVisible(true);
            }}
          >
            Add Member
          </Button>
        </Col>
      </Row>

      <Table
        columns={columns}
        dataSource={filteredData}
        rowKey="_id"
        pagination={pagination}
        onChange={setPagination}
      />

      <Modal
        visible={!!imagePreview}
        footer={null}
        onCancel={() => setImagePreview(null)}
      >
        <img alt="Preview" style={{ width: '100%' }} src={imagePreview} />
      </Modal>

      <Modal
        title={editMode ? 'Edit Member' : 'Add Member'}
        visible={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        okText={editMode ? 'Update' : 'Add'}
        width={1000}
      >
        <Form form={form} layout="vertical">
          {/* Row 1 */}
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="Name" label="Name" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="Mobileno" label="Mobile No" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="MobileAlternativeno" label="Alternative Mobile No">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          {/* Row 2 */}
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="Email" label="Email">
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="CompanyName" label="Company Name">
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="BusinessCategory" label="Business Category">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          {/* Row 3 */}
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="groupincharge" label="Group Incharge">
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="City1" label="City 1">
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="City2" label="City 2">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          {/* Row 4 */}
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="Village1" label="Village 1">
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="Village2" label="Village 2">
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="joiningdate" label="Joining Date">
                <DatePicker format="YYYY-MM-DD" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          {/* Row 5 */}
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="Dob" label="Date of Birth">
                <DatePicker format="YYYY-MM-DD" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="WeddingAnnversery" label="Wedding Anniversary">
                <DatePicker format="YYYY-MM-DD" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="Address" label="Address">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          {/* Row 6 */}
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="profile"
                label="Profile Picture"
                valuePropName="fileList"
                getValueFromEvent={(e) => (Array.isArray(e) ? e : e?.fileList)}
              >
                <Upload
                  listType="picture-card"
                  maxCount={1}
                  beforeUpload={() => false}
                  onPreview={(file) => setImagePreview(file.url || file.thumbUrl)}
                >
                  <Button icon={<UploadOutlined />}>Upload</Button>
                </Upload>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="registerForm"
                label="Register Form"
                valuePropName="fileList"
                getValueFromEvent={(e) => (Array.isArray(e) ? e : e?.fileList)}
              >
                <Upload
                  listType="picture-card"
                  maxCount={1}
                  beforeUpload={() => false}
                  onPreview={(file) => setImagePreview(file.url || file.thumbUrl)}
                >
                  <Button icon={<UploadOutlined />}>Upload</Button>
                </Upload>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="certificate"
                label="Certificate"
                valuePropName="fileList"
                getValueFromEvent={(e) => (Array.isArray(e) ? e : e?.fileList)}
              >
                <Upload
                  listType="picture-card"
                  maxCount={1}
                  beforeUpload={() => false}
                  onPreview={(file) => setImagePreview(file.url || file.thumbUrl)}
                >
                  <Button icon={<UploadOutlined />}>Upload</Button>
                </Upload>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default Member;